import * as React from 'react';
import * as moment from 'moment';
import { withStyles, createStyles, Theme } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import TextField from '@material-ui/core/TextField';
import Question from './Question';
import QuizPreview from './QuizPreview';
import SuccessDialog from '../Common/SuccessDialog';
import FailDialog from '../Common/FailDialog';
import PageTitle from '../Common/PageTitle';

const jss = (theme: Theme) => createStyles({
    container: {
        display: 'flex',
        flexWrap: 'wrap',
        width: '100%',
        height: '100%',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
    },
    date: {
        display: 'flex',
        flexWrap: 'wrap',
        width: '100%',
        marginTop: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit,
    },
    question: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        margin: '10px 0px',
        padding: '20px',
        backgroundColor: theme.palette.secondary.main,
    },
    divider: {
        margin: `${theme.spacing.unit * 2}px 0px`,
        width: '100%',
        backgroundColor: theme.palette.primary.main,
    },
    preview: {
        marginTop: theme.spacing.unit * 4,
        marginBottom: theme.spacing.unit,
    },
    remove: {
        color: theme.palette.error.light,
        borderColor: theme.palette.error.light,
        '&:disabled': {
            borderColor: theme.palette.action.disabled,
        }
    },
    questionBtn: {
        margin: '5px',
    },
    questionBtns: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
    },
});

interface Props {
    edit?: boolean;
    create?: boolean;
    quiz?: Bee.Quiz
    back?: string;
    onQuizSave?: (quiz: Bee.QuizCore) => Promise<Bee.ClientResponse> | undefined;
    onQuizCreate?: (quiz: Bee.QuizCore) => any;
    match: {
        path: string;
        params: {
            quizId?: string;
        };
    };
    history: { push: (path: string) => void; };
    location: { hash: string };
    classes: {
        container: string;
        date: string;
        question: string;
        divider: string;
        preview: string;
        remove: string;
        questionBtn: string;
        questionBtns: string;
    };
}

interface State {
    title: string;
    startTime: string;
    endTime: string;
    questions: Bee.Question[];
    answers: Bee.Answer[];
    showPreview: boolean;
    fail: boolean;
    success: boolean;
    error: string;
}

class AdminCreate extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = this.getInitialState(props);
    }

    private getInitialState = (props: Props): State => {
        const state = {
            title: '',
            startTime: '',
            endTime: '',
            questions: [
                this.getQuestion(),
            ],
            answers: [
                this.getAnswer(),
            ],
            showPreview: false,
            fail: false,
            success: false,
            error: '', 
        }

        const { quiz } = props;

        if (quiz) {
            state.title = quiz.title,
            state.startTime = moment(quiz.startTime).format("YYYY-MM-DDTHH:mm");
            state.endTime = moment(quiz.endTime).format("YYYY-MM-DDTHH:mm");
            state.questions = [...quiz.questions];
            state.answers = [...quiz.answers];
        }

        return state;
    }

    private canRemoveQ = () => {
        return (this.state.questions.length > 1 && this.state.answers.length > 1);
    }

    private onAdd = () => {
        this.setState({
            questions: [...this.state.questions, this.getQuestion() ],
            answers: [...this.state.answers, this.getAnswer()],
        });
    }

    private onRemove = () => {
        if (!this.canRemoveQ()) {
            return;
        }
        const questions = [...this.state.questions];
        const answers = [...this.state.answers];
        this.setState({
            questions: questions.slice(0, -1),
            answers: answers.slice(0, -1),
        });
    }

    private onTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ title: e.target.value });
    }

    private startTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ startTime: e.target.value });
    }

    private endTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ endTime: e.target.value });
    }
    
    private getQuestion = (): Bee.Question => {
        return {
            url: '',
            type: 'video',
            provider: 'youtube',
            options: ['', ''],
        }
    };

    private getAnswer = (): Bee.Answer => {
        return {
            correctOption: '',
            description: '',
        }
    };

    private addOption = (questionIx: number) => (e: React.ChangeEvent<HTMLButtonElement>) => {
        const questions = [...this.state.questions];
        questions[questionIx].options.push('');
        this.setState({ questions });
    }

    private removeOption = (questionIx: number) => (e: React.ChangeEvent<HTMLButtonElement>) => {
        const questions = [...this.state.questions];
        questions[questionIx].options.pop();
        this.setState({ questions });
    }

    private onLinkChange = (questionIx: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const questions = [...this.state.questions];
        questions[questionIx].url = e.target.value;
        this.setState({ questions });
    }

    private onAnswerChange = (answerIx: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const answers = [...this.state.answers];
        answers[answerIx].correctOption = e.target.value;
        this.setState({ answers });
    }

    private onExplanationChange = (answerIx: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const answers = [...this.state.answers];
        answers[answerIx].description = e.target.value;
        this.setState({ answers });
    }

    private onOptionChange = (questionIx: number) => (optionIx: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const questions = [...this.state.questions];
        questions[questionIx].options[optionIx] = e.target.value;
        this.setState({ questions });
    }

    private onDialogClose = () => {
        if (this.state.fail) {
            this.setState({ fail: false, error: '' });
        } else {
            this.props.history.push(`${'/admin'}${this.props.location.hash}`);
        }
    }

    private onSubmit = async () => {
        this.setState({ showPreview: false });
        const quiz = this.getFormattedQuiz();
        let data;
        if (this.props.create && this.props.onQuizCreate) {
            data = await this.props.onQuizCreate(quiz);
        } else if (this.props.edit && this.props.onQuizSave) {
            data = await this.props.onQuizSave(quiz);
        } else {
            return;
        }
        
        if (!data || data.status !== 200) {
            this.setState({ fail: true, error: data ? data.data : '' });
        } else {
            this.setState({ success: true });
        }
    }

    private getFormattedTime = (inputTime: string): string => {
        if (!inputTime) {
            return '';
        }
        const date = new Date(inputTime);
        return date.toISOString();
    }

    private getFormattedQuiz = (): Bee.QuizCore => {
        const quiz = {...this.state};

        const start = this.getFormattedTime(quiz.startTime);
        const end = this.getFormattedTime(quiz.endTime);

        quiz.startTime = start;
        quiz.endTime = end;
        
        delete quiz.showPreview;
        
        return quiz;
    }

    private getQuizPreview = () => {
        const quiz = this.getFormattedQuiz() as Bee.Quiz;
        quiz.id = 'preview-quiz';
        return quiz;
    }

    private showPreview = () => {
        this.setState({ showPreview: true });
    }

    private hidePreview = () => {
        this.setState({ showPreview: false });
    }

  	render() {
        const { classes } = this.props;
        const viewOnly = !this.props.create && !this.props.edit;
        const pageTitle = viewOnly ? 'View Quiz' : this.props.create ? 'Create New Quiz' : 'Edit Quiz';
        
        return (
            <div className={classes.container}>
                <PageTitle title={pageTitle} back={this.props.back} />
                <TextField
                    label="Title"
                    placeholder="New Quiz Title"
                    margin="normal"
                    variant="outlined"
                    fullWidth
                    required
                    disabled={viewOnly}
                    value={this.state.title}
                    onChange={this.onTitleChange}
                />
                <TextField
                    type="datetime-local"
                    margin="normal"
                    variant="outlined"
                    label="Start Time"
                    required
                    InputLabelProps={{
                        shrink: true,
                    }}
                    fullWidth
                    disabled={viewOnly}
                    value={this.state.startTime}
                    onChange={this.startTimeChange}
                />

                <TextField
                    type="datetime-local"
                    margin="normal"
                    variant="outlined"
                    label="End Time"
                    required
                    InputLabelProps={{
                        shrink: true,
                    }}
                    fullWidth
                    disabled={viewOnly}
                    value={this.state.endTime}
                    onChange={this.endTimeChange}
                />

                {this.state.questions.map((q, ix) => (
                    <React.Fragment key={ix}>
                        <Divider className={classes.divider} />
                        <Question
                            disabled={viewOnly}
                            number={ix + 1}
                            answer={this.state.answers[ix]}
                            question={q}
                            addOption={this.addOption(ix)}
                            removeOption={this.removeOption(ix)}
                            onLinkChange={this.onLinkChange(ix)}
                            onAnswerChange={this.onAnswerChange(ix)}
                            onExplanationChange={this.onExplanationChange(ix)}
                            onOptionChange={this.onOptionChange(ix)}
                        />
                    </React.Fragment>
                ))}

                <Divider className={classes.divider} />

                <div className={classes.questionBtns}>
                    <Button
                        className={classes.questionBtn}
                        variant="outlined"
                        color="primary"
                        onClick={this.onAdd}
                        disabled={viewOnly}
                    >
                        Add Question
                    </Button>
                    <Button
                        className={`${classes.remove} ${classes.questionBtn}`}
                        variant="outlined"
                        onClick={this.onRemove}
                        disabled={!this.canRemoveQ() || viewOnly}
                    >
                        Remove Question
                    </Button>
                </div>

                <Button
                    className={classes.preview}
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={viewOnly}
                    onClick={this.showPreview}
                >
                    Preview
                </Button>

                {this.state.showPreview ?
                    <QuizPreview
                        open={this.state.showPreview}
                        onClose={this.hidePreview}
                        onSubmit={this.onSubmit}
                        edit={this.props.edit}
                        quiz={this.getQuizPreview()}
                        match={this.props.match}
                        location={this.props.location}
                    />
                : null}
                <FailDialog
                    open={this.state.fail}
                    onClose={this.onDialogClose}
                    message={this.state.error || 'Quiz creation failed.'}
                />
                <SuccessDialog
                    open={this.state.success}
                    onClose={this.onDialogClose}
                    message={`Quiz was successfully ${this.props.edit ? 'saved' : 'created'}.`}
                />
            </div>
        )
	}
}

export default withStyles(jss)(AdminCreate);



// WEBPACK FOOTER //
// ./src/components/Admin/AdminQuiz.tsx